import React, { useEffect, useRef, useState } from 'react';
import { api } from '../api';

const dict = {
  en: {
    title: 'Ask Pythia',
    sub: 'Multi-turn Q&A on this specific report. Cites your risk stack, AVM, red flags, and enrichments only.',
    placeholder: 'e.g. What if I offer $600k? What is my strongest lever?',
    send: 'Ask',
    thinking: 'Pythia is thinking…',
    empty: 'No questions yet. Ask anything about verdict, AVM, risks, or negotiation.',
    disclaimer: 'Not investment advice. Answers are grounded strictly in the report above.',
    suggested: 'Try:',
    s1: 'What if I offer $600k?',
    s2: 'What is my strongest negotiation lever?',
    s3: 'What is the biggest risk if I proceed?',
    s4: 'Should I request an inspection contingency?',
  },
  es: {
    title: 'Pregúntale a Pythia',
    sub: 'Q&A multi-turno sobre este reporte específico. Cita solo el risk stack, AVM, red flags y enrichments de arriba.',
    placeholder: 'ej. ¿Y si ofrezco $600k? ¿Cuál es mi palanca más fuerte?',
    send: 'Preguntar',
    thinking: 'Pythia está pensando…',
    empty: 'Sin preguntas aún. Pregunta sobre veredicto, AVM, riesgos o negociación.',
    disclaimer: 'No es asesoría de inversión. Las respuestas están ancladas al reporte de arriba.',
    suggested: 'Prueba:',
    s1: '¿Y si ofrezco $600k?',
    s2: '¿Cuál es mi palanca de negociación más fuerte?',
    s3: '¿Cuál es el mayor riesgo si procedo?',
    s4: '¿Debo pedir contingencia de inspección?',
  },
};

export default function ReportQA({ reportId, shareToken, isAdmin, lang = 'en' }) {
  const t = dict[lang] || dict.en;
  const [conversationId, setConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages.length, loading]);

  const send = async (questionText) => {
    const q = (questionText ?? input).trim();
    if (!q || loading) return;
    setInput('');
    setErr('');
    setLoading(true);
    // Optimistic append
    setMessages(m => [...m, { role: 'user', content: q }]);
    try {
      const headers = isAdmin
        ? { 'X-Admin-Token': localStorage.getItem('pythia_admin_token') || '' }
        : {};
      const payload = { question: q, lang, conversation_id: conversationId };
      if (!isAdmin && shareToken) payload.share_token = shareToken;
      const res = await api.post(`/reports/${reportId}/qa`, payload, { headers });
      const { conversation_id, answer } = res.data;
      setConversationId(conversation_id);
      setMessages(m => [...m, { role: 'assistant', content: answer }]);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Q&A failed');
      // Roll back the optimistic user message
      setMessages(m => m.slice(0, -1));
    }
    setLoading(false);
  };

  const canAsk = isAdmin || shareToken;
  if (!canAsk) return null;

  return (
    <div className="lg:col-span-12 border hairline bg-surface1" data-testid="report-qa">
      <div className="border-b hairline px-6 py-4 flex items-center justify-between gap-4">
        <div>
          <div className="mono text-[10px] uppercase tracking-widest2 text-gold">§ ASK PYTHIA</div>
          <h3 className="serif text-2xl mt-1 text-white">{t.title}</h3>
        </div>
        <div className="mono text-[10px] uppercase tracking-widest2 text-white/40 max-w-xs text-right hidden md:block">{t.sub}</div>
      </div>

      <div ref={scrollRef} className="max-h-[420px] overflow-y-auto p-6 space-y-4">
        {messages.length === 0 && (
          <div className="text-center py-6">
            <div className="mono text-xs text-white/40 mb-6">{t.empty}</div>
            <div className="mono text-[10px] uppercase tracking-widest2 text-white/50 mb-3">{t.suggested}</div>
            <div className="flex flex-wrap justify-center gap-2 max-w-2xl mx-auto">
              {[t.s1, t.s2, t.s3, t.s4].map((s, i) => (
                <button
                  key={i}
                  onClick={() => send(s)}
                  className="mono text-[10px] uppercase tracking-widest2 border hairline px-3 py-1.5 text-white/70 hover:border-gold hover:text-gold"
                  style={{transitionProperty: 'color, border-color'}}
                  data-testid={`qa-suggested-${i}`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex gap-3 ${m.role === 'user' ? 'justify-end' : ''}`} data-testid={`qa-msg-${i}`}>
            {m.role === 'assistant' && <div className="mono text-[10px] uppercase tracking-widest2 text-gold shrink-0 pt-1">Pythia</div>}
            <div className={`max-w-[75%] px-4 py-3 border ${m.role === 'user' ? 'bg-obsidian border-gold/40 text-white' : 'hairline bg-obsidian/60 text-white/90'}`}>
              <div className="text-sm leading-relaxed whitespace-pre-wrap">{m.content}</div>
            </div>
            {m.role === 'user' && <div className="mono text-[10px] uppercase tracking-widest2 text-white/40 shrink-0 pt-1">You</div>}
          </div>
        ))}
        {loading && (
          <div className="flex gap-3">
            <div className="mono text-[10px] uppercase tracking-widest2 text-gold shrink-0 pt-1">Pythia</div>
            <div className="max-w-[75%] px-4 py-3 border hairline bg-obsidian/60">
              <div className="mono text-xs text-white/50">{t.thinking}<span className="blink">_</span></div>
            </div>
          </div>
        )}
      </div>

      <form onSubmit={e => { e.preventDefault(); send(); }} className="border-t hairline p-4 flex gap-2" data-testid="qa-form">
        <input
          value={input}
          onChange={e => setInput(e.target.value)}
          placeholder={t.placeholder}
          className="flex-1 bg-obsidian border hairline px-4 py-3 mono text-sm text-white placeholder-white/30 focus:border-gold outline-none"
          style={{transitionProperty: 'border-color'}}
          disabled={loading}
          data-testid="qa-input"
        />
        <button
          type="submit"
          disabled={loading || !input.trim()}
          className="px-6 py-3 bg-gold text-obsidian mono text-[11px] uppercase tracking-widest2 hover:bg-white disabled:opacity-40"
          style={{transitionProperty: 'background-color'}}
          data-testid="qa-send"
        >
          {t.send} →
        </button>
      </form>

      {err && <div className="border-t hairline px-4 py-2 mono text-xs text-red-400">{err}</div>}
      <div className="border-t hairline px-4 py-2 mono text-[10px] uppercase tracking-widest2 text-white/40">{t.disclaimer}</div>
    </div>
  );
}
